import React from "react";
import PropTypes from "prop-types";
import Badge from "../../../../components/ui-components/Badge.jsx";
import { fmtEurSigned } from "../../blocks/format";

// DriverOverview - the READ-ONLY driver list shown when the driver_table block is selected as a
// whole (blockInspector.js's 'driverOverview' kind). Mirrors the printed Key-drivers table's
// identifying columns (driver · impact · category) and lets a reviewer drill into one driver to
// edit its annotation - the same action a driver-row click on the report canvas performs. Rows are
// keyboard-operable role="button" divs (not <button>) to sidestep the global `button { all: unset }`
// reset (see CLAUDE.md reference).

const CATEGORY_LABELS = { volume: "Volume", rate: "Rate" };
const CATEGORY_BADGE_STATUS = { volume: "info", rate: "neutral" };

function categoryLabel(category) {
  if (!category) return null;
  if (CATEGORY_LABELS[category]) return CATEGORY_LABELS[category];
  return category
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// impactTone - positive impact made spend worse (adverse/red), negative helped (favorable/green).
function impactTone(impact) {
  if (typeof impact !== "number" || impact === 0) return "text-text-2";
  return impact > 0 ? "text-danger" : "text-success";
}

// hasAnnotation - whether a reviewer has already filled in any of the editable notes, so the list
// can flag which drivers still need attention.
function hasAnnotation(d) {
  return Boolean(d && (d.cause || d.expected || d.owner || d.action));
}

const ChevronRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="flex-shrink-0 text-text-3">
    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DriverOverview = ({ drivers, onSelect }) => {
  const rows = Array.isArray(drivers) ? drivers : [];
  if (rows.length === 0) {
    return <p className="text-xs text-text-3">No drivers recorded for this period.</p>;
  }

  return (
    <ul className="-mx-2">
      {rows.map((d, index) => {
        const label = categoryLabel(d.category);
        const annotated = hasAnnotation(d);
        const select = () => onSelect(index);
        return (
          // eslint-disable-next-line react/no-array-index-key
          <li key={d.driver || index}>
            <div
              role="button"
              tabIndex={0}
              aria-label={`Edit annotation for ${d.driver || `driver ${index + 1}`}`}
              onClick={select}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  select();
                }
              }}
              className="flex items-center gap-2.5 px-2 py-2.5 rounded-ctrl cursor-pointer transition-colors hover:bg-background-grey focus:outline-none focus:bg-background-grey"
            >
              <span
                className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${annotated ? "bg-primary" : "bg-border"}`}
                title={annotated ? "Annotated" : "Not yet annotated"}
                aria-hidden="true"
              />
              <span className="flex-1 min-w-0 text-xs font-medium text-text truncate">
                {d.driver || `Driver ${index + 1}`}
              </span>
              {label && (
                <Badge label={label} status={CATEGORY_BADGE_STATUS[d.category] || "neutral"} size="sm" />
              )}
              <span className={`text-xs font-semibold tabular-nums ${impactTone(d.impact)}`}>
                {typeof d.impact === "number" ? fmtEurSigned(d.impact) : "—"}
              </span>
              <ChevronRight />
            </div>
          </li>
        );
      })}
    </ul>
  );
};

DriverOverview.propTypes = {
  drivers: PropTypes.arrayOf(
    PropTypes.shape({
      driver: PropTypes.string,
      impact: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      category: PropTypes.string,
      cause: PropTypes.string,
      expected: PropTypes.string,
      owner: PropTypes.string,
      action: PropTypes.string,
    })
  ),
  onSelect: PropTypes.func.isRequired,
};

export default DriverOverview;
